import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Badge, 
    Spinner, Alert, Tooltip, OverlayTrigger, Modal 
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrency, formatDate } from "../utils/formatters";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

export default function Fournisseurs() {
    const navigate = useNavigate();
    const [fournisseurs, setFournisseurs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'raison_sociale', direction: 'asc' });
    const [showEvalModal, setShowEvalModal] = useState(false);
    const [selectedFournisseur, setSelectedFournisseur] = useState(null);
    const [evaluation, setEvaluation] = useState({
        note: 5,
        critere_delai: 5,
        critere_qualite: 5,
        critere_prix: 5,
        commentaire: ''
    });
    const [evalLoading, setEvalLoading] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        fetchFournisseurs();
        fetchStats();
    }, []);

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
    };

    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorAlert(true);
        setTimeout(() => setShowErrorAlert(false), 3000);
    };

    const fetchFournisseurs = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/fournisseurs.php',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setFournisseurs(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/fournisseurs.php?action=stats',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce fournisseur ?")) return;
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/fournisseurs.php?id=${id}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            
            const result = await response.json();
            
            if (result.success) {
                fetchFournisseurs();
                fetchStats();
                showSuccess("✅ Fournisseur supprimé avec succès");
            } else {
                showError(result.message);
            }
        } catch (err) {
            showError("Erreur lors de la suppression");
        }
    };

    const handleEvaluer = (fournisseur) => {
        setSelectedFournisseur(fournisseur);
        setEvaluation({
            note: 5,
            critere_delai: 5,
            critere_qualite: 5,
            critere_prix: 5,
            commentaire: ''
        });
        setShowEvalModal(true);
    };

  const submitEvaluation = async () => {
    setEvalLoading(true);
    try {
        const token = localStorage.getItem("token");
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        
        const response = await fetch('https://sawd.julesferry-ga.com/api/fournisseurs.php', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                action: 'evaluer',
                id_fournisseur: selectedFournisseur.id_fournisseur,
                ...evaluation,
                id_utilisateur: user.id_utilisateur || 1
            })
        });
        
        const result = await response.json();
        
        if (result.success) {
            setShowEvalModal(false);
            showSuccess("⭐ Évaluation enregistrée avec succès");
            // Recharger les fournisseurs pour voir la nouvelle note
            fetchFournisseurs(); // ← AJOUTEZ CETTE LIGNE
            fetchStats();        // ← AJOUTEZ CETTE LIGNE
        } else {
            showError(result.message);
        }
    } catch (err) {
        showError("Erreur lors de l'évaluation");
    } finally {
        setEvalLoading(false);
    }
};

    const getNoteStars = (note) => {
    // Convertir en nombre et arrondir
    const noteValue = note ? Math.round(parseFloat(note)) : 0;
    const stars = [];
    for (let i = 1; i <= 5; i++) {
        stars.push(
            <i key={i} className={`ri-star-${i <= noteValue ? 'fill' : 'line'} text-warning`} style={{ fontSize: '0.9rem' }}></i>
        );
    }
    return stars;
};

    const getStatutBadge = (fournisseur) => {
        if (fournisseur.nb_commandes > 0) {
            return (
                <Badge bg="success" className="px-3 py-2 rounded-pill">
                    <i className="ri-checkbox-circle-line me-1"></i>Actif
                </Badge>
            );
        }
        return (
            <Badge bg="secondary" className="px-3 py-2 rounded-pill">
                <i className="ri-user-unfollow-line me-1"></i>Inactif
            </Badge>
        );
    };

    const filteredFournisseurs = useMemo(() => {
        return fournisseurs
            .filter(f => 
                f.raison_sociale?.toLowerCase().includes(filterText.toLowerCase()) ||
                f.code_fournisseur?.toLowerCase().includes(filterText.toLowerCase()) ||
                f.email?.toLowerCase().includes(filterText.toLowerCase()) ||
                f.telephone?.includes(filterText)
            )
            .sort((a, b) => {
                if (sortConfig.key === 'raison_sociale') {
                    return sortConfig.direction === 'asc' 
                        ? a.raison_sociale?.localeCompare(b.raison_sociale)
                        : b.raison_sociale?.localeCompare(a.raison_sociale);
                }
                if (sortConfig.key === 'total_achats') {
                    return sortConfig.direction === 'asc' 
                        ? (a.total_achats || 0) - (b.total_achats || 0)
                        : (b.total_achats || 0) - (a.total_achats || 0);
                }
                return 0;
            });
    }, [fournisseurs, filterText, sortConfig]);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)} 
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const ActionButton = ({ variant, icon, onClick, tooltip }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
            <Button 
                variant={variant} 
                size="sm" 
                onClick={onClick} 
                className="action-btn"
               
            >
                <i className={icon} style={{ fontSize: '1rem' }}></i>
            </Button>
        </OverlayTrigger>
    );

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                            <p className="text-muted">Chargement des fournisseurs...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error}</h4>
                        <Button variant="outline-danger" className="mt-3" onClick={fetchFournisseurs}>
                            <i className="ri-refresh-line me-2"></i>Réessayer
                        </Button>
                    </Alert>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* Alertes */}
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {successMessage}
                        </Alert>
                    )}
                    
                    {showErrorAlert && (
                        <Alert variant="danger" onClose={() => setShowErrorAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {errorMessage}
                        </Alert>
                    )}

                    {/* En-tête avec design moderne */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div 
                                        className="page-icon-wrapper d-flex align-items-center justify-content-center" 
                        style={{ 
                            width: '70px', 
                            height: '70px', 
                            borderRadius: '20px',
                            background: 'linear-gradient(135deg, rgba(67, 97, 238, 0.15), rgba(58, 86, 212, 0.15))',
                            boxShadow: '0 10px 20px rgba(67, 97, 238, 0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}
                    >
                                        <i className="ri-truck-line text-primary" style={{ fontSize: '36px', lineHeight: 1 }}></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1" style={{ fontSize: '2rem', fontWeight: 700, color: '#1e293b' }}>
                                            Fournisseurs
                                        </h1>
                                        <p className="page-subtitle mb-0" style={{ fontSize: '1rem', color: '#64748b' }}>
                                            {stats ? `${stats.total_fournisseurs || 0} fournisseurs enregistrés` : 'Gérez vos fournisseurs et leurs évaluations'}
                                        </p>
                                    </div>
                                </div>
                                <Button 
                                    variant="primary" 
                                    onClick={() => navigate('/achats/fournisseursAjouter')}
                                    className="btn-gradient px-4 py-2 d-flex align-items-center gap-2"
                                    style={{
                                        background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                        border: 'none',
                                        borderRadius: '12px',
                                        padding: '0.6rem 1.5rem',
                                        fontWeight: 500,
                                        boxShadow: '0 8px 16px rgba(67, 97, 238, 0.25)',
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    <i className="ri-add-line" style={{ fontSize: '1.2rem' }}></i>
                                    <span>Nouveau fournisseur</span>
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Statistiques modernes */}
                    {stats && (
                        <Row className="g-3 mb-4 stats-row">
                            <Col md={4}>
                                <Card className="stat-card border-0 shadow-sm h-100">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div 
                                                className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                                                style={{ 
                                                    width: '60px', 
                                                    height: '60px', 
                                                    borderRadius: '16px',
                                                    background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                                    boxShadow: '0 10px 20px rgba(67, 97, 238, 0.25)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <i className="ri-truck-line text-white" style={{ fontSize: '30px', lineHeight: 1 }}></i>
                                            </div>
                                            <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                                                <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                                                    Total fournisseurs
                                                </span>
                                                <div className="d-flex align-items-baseline gap-2">
                                                    <h2 className="stat-value text-primary mb-0 fw-bold" style={{ fontSize: '2rem', lineHeight: 1.2 }}>
                                                        {stats.total_fournisseurs || 0}
                                                    </h2>
                                                    <span className="badge bg-primary bg-opacity-10 text-primary px-2 py-1 rounded-pill">
                                                        <i className="ri-user-add-line me-1"></i>
                                                        +{stats.nouveaux_ajd || 0}
                                                    </span>
                                                </div>
                                                <small className="stat-trend text-primary d-flex align-items-center gap-1 mt-2">
                                                    <i className="ri-arrow-up-line"></i>
                                                    <span className="text-truncate">nouveaux aujourd'hui</span>
                                                </small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="stat-card border-0 shadow-sm h-100">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div 
                                                className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                                                style={{ 
                                                    width: '60px', 
                                                    height: '60px', 
                                                    borderRadius: '16px',
                                                    background: 'linear-gradient(135deg, #10b981, #059669)',
                                                    boxShadow: '0 10px 20px rgba(16, 185, 129, 0.25)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <i className="ri-checkbox-circle-line text-white" style={{ fontSize: '30px', lineHeight: 1 }}></i>
                                            </div>
                                            <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                                                <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                                                    Fournisseurs actifs
                                                </span>
                                                <div className="d-flex align-items-baseline gap-2">
                                                    <h2 className="stat-value text-white mb-0 fw-bold" style={{ fontSize: '2rem', lineHeight: 1.2 }}>
                                                        {stats.fournisseurs_actifs || 0}
                                                    </h2>
                                                    <span className="badge bg-success bg-opacity-10 text-white px-2 py-1 rounded-pill">
                                                        <i className="ri-calendar-line me-1"></i>
                                                        30j
                                                    </span>
                                                </div>
                                                <small className="stat-trend text-success d-flex align-items-center gap-1 mt-2">
                                                    <i className="ri-check-line"></i>
                                                    <span className="text-truncate">avec commandes récentes</span>
                                                </small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                           {/* Note moyenne */}
<Col md={4}>
    <Card className="stat-card border-0 shadow-sm h-100">
        <Card.Body className="p-3">
            <div className="d-flex align-items-center">
                <div 
                    className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                    style={{ 
                        width: '60px', 
                        height: '60px', 
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                        boxShadow: '0 10px 20px rgba(245, 158, 11, 0.25)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                    }}
                >
                    <i className="ri-star-line text-white" style={{ fontSize: '30px', lineHeight: 1 }}></i>
                </div>
                <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                    <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                        Note moyenne
                    </span>
                    <div className="d-flex align-items-baseline gap-2">
                        <h2 className="stat-value text-warning mb-0 fw-bold" style={{ fontSize: '2rem', lineHeight: 1.2 }}>
                            {stats.note_moyenne ? parseFloat(stats.note_moyenne).toFixed(1) : 'N/A'}
                        </h2>
                        <span className="badge bg-warning bg-opacity-10 text-warning px-2 py-1 rounded-pill">
                            <i className="ri-star-fill me-1"></i>
                            /5
                        </span>
                    </div>
                    <small className="stat-trend text-warning d-flex align-items-center gap-1 mt-2">
                        <i className="ri-star-line"></i>
                        <span className="text-truncate">satisfaction fournisseurs</span>
                    </small>
                </div>
            </div>
        </Card.Body>
    </Card>
</Col>
                        </Row>
                    )}

                    {/* Filtres modernes */}
                    <Card className="filter-card border-0 mb-4">
                        <Card.Body>
                            <Row className="g-3">
                                <Col md={8}>
                                    <InputGroup className="search-input">
                                        <InputGroup.Text className="bg-white border-end-0">
                                            <i className="ri-search-line text-muted"></i>
                                        </InputGroup.Text>
                                        <Form.Control
                                            placeholder="Rechercher par nom, code, email, téléphone..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="border-start-0"
                                        />
                                        {filterText && (
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => setFilterText('')}
                                                className="border-0"
                                            >
                                                <i className="ri-close-line"></i>
                                            </Button>
                                        )}
                                    </InputGroup>
                                </Col>
                                <Col md={4} className="d-flex justify-content-end align-items-center">
                                    <div className="filter-info">
                                        <span className="badge-count bg-primary text-white px-3 py-1 rounded-pill">
                                            {filteredFournisseurs.length}
                                        </span>
                                        <span className="text-muted ms-2">fournisseur(s) trouvé(s)</span>
                                    </div>
                                    <Button
                                        variant="link"
                                        className="text-muted ms-3"
                                        onClick={() => setShowFilters(!showFilters)}
                                    >
                                        <i className={`ri-equalizer-line fs-5 ${showFilters ? 'text-primary' : ''}`}></i>
                                    </Button>
                                </Col>
                            </Row>
                            
                            {showFilters && (
                                <Row className="mt-3">
                                    <Col md={3}>
                                        <Form.Select className="filter-select">
                                            <option>Trier par nom</option>
                                            <option>Plus d'achats</option>
                                            <option>Moins d'achats</option>
                                            <option>Note la plus haute</option>
                                        </Form.Select>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Select className="filter-select">
                                            <option>Tous les statuts</option>
                                            <option>Actifs</option>
                                            <option>Inactifs</option>
                                        </Form.Select>
                                    </Col>
                                </Row>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Tableau moderne */}
                    <Card className="table-card border-0">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            {/* <th className="py-3">Code</th> */}
                                            <SortableHeader field="raison_sociale">Fournisseur</SortableHeader>
                                            <th>Contact</th>
                                            <SortableHeader field="total_achats">Total achats</SortableHeader>
                                            <th>Derni. com.</th>
                                            <th>Note</th>
                                            <th>Statut</th>
                                            <th className="text-center py-3">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredFournisseurs.length > 0 ? (
                                            filteredFournisseurs.map((f, index) => (
                                                <tr key={f.id_fournisseur} className="fournisseur-row" style={{
                                                    animation: `fadeIn 0.3s ease ${index * 0.05}s both`
                                                }}>
                                                    {/* <td>
                                                        <Badge bg="light" text="dark" className="px-3 py-2 rounded-pill font-mono">
                                                            {f.code_fournisseur}
                                                        </Badge>
                                                    </td> */}
                                                    <td>
                                                        <Link to={`/achats/fournisseurs/${f.id_fournisseur}`} className="text-primary fw-semibold text-decoration-none">
                                                            {f.raison_sociale}
                                                        </Link>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-column">
                                                            {f.telephone && (
                                                                <small className="text-truncate" style={{ maxWidth: '150px' }}>
                                                                    <i className="ri-phone-line text-muted me-1"></i>
                                                                    {f.telephone}
                                                                </small>
                                                            )}
                                                            {f.email && (
                                                                <small className="text-truncate" style={{ maxWidth: '150px' }}>
                                                                    <i className="ri-mail-line text-muted me-1"></i>
                                                                    {f.email}
                                                                </small>
                                                            )}
                                                            {!f.telephone && !f.email && <span className="text-muted">-</span>}
                                                        </div>
                                                    </td>
                                                    <td className="fw-bold text-primary">
                                                        {formatCurrency(f.total_achats || 0)}
                                                    </td>
                                                    <td>
                                                        {f.derniere_commande ? (
                                                            <span className="text-muted">
                                                                <i className="ri-calendar-line me-1"></i>
                                                                {formatDate(f.derniere_commande)}
                                                            </span>
                                                        ) : '-'}
                                                    </td>
                                                    <td>
    <div className="text-nowrap">
        {getNoteStars(f.note_moyenne ? Math.round(parseFloat(f.note_moyenne)) : 0)}
        {f.note_moyenne && (
            <small className="ms-1 text-muted">
                ({parseFloat(f.note_moyenne).toFixed(1)})
            </small>
        )}
    </div>
</td>
                                                    <td>{getStatutBadge(f)}</td>
                                                    <td className="text-center">
                                                        <div className="d-flex justify-content-center gap-2">
                                                            <ActionButton
                                                                variant="outline-info"
                                                                icon="ri-eye-line"
                                                                onClick={() => navigate(`/achats/fournisseurs/${f.id_fournisseur}`)}
                                                                tooltip="Voir détails"
                                                            />
                                                            <ActionButton
                                                                variant="outline-primary"
                                                                icon="ri-pencil-line"
                                                                onClick={() => navigate(`/achats/fournisseurs/modifier/${f.id_fournisseur}`)}
                                                                tooltip="Modifier"
                                                            />
                                                            <ActionButton
                                                                variant="outline-warning"
                                                                icon="ri-star-line"
                                                                onClick={() => handleEvaluer(f)}
                                                                tooltip="Évaluer"
                                                            />
                                                            <ActionButton
                                                                variant="outline-danger"
                                                                icon="ri-delete-bin-line"
                                                                onClick={() => handleDelete(f.id_fournisseur)}
                                                                tooltip="Supprimer"
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center py-5">
                                                    <div className="empty-state">
                                                        <i className="ri-truck-line empty-icon text-primary" style={{ fontSize: '4rem' }}></i>
                                                        <h5 className="mt-3">Aucun fournisseur trouvé</h5>
                                                        <p className="text-muted mb-3">
                                                            {filterText 
                                                                ? "Aucun résultat ne correspond à votre recherche"
                                                                : "Commencez par ajouter votre premier fournisseur"}
                                                        </p>
                                                        {filterText ? (
                                                            <Button 
                                                                variant="outline-primary"
                                                                onClick={() => setFilterText('')}
                                                            >
                                                                <i className="ri-refresh-line me-2"></i>Réinitialiser
                                                            </Button>
                                                        ) : (
                                                            <Button 
                                                                variant="primary"
                                                                onClick={() => navigate('/achats/fournisseursAjouter')}
                                                            >
                                                                <i className="ri-add-line me-2"></i>Ajouter un fournisseur
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </div>

                {/* Modal d'évaluation modernisé */}
                <Modal 
                    show={showEvalModal} 
                    onHide={() => setShowEvalModal(false)} 
                    centered 
                    className="modern-modal"
                >
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    <i className="ri-star-line text-warning" style={{ fontSize: '4rem' }}></i>
                                </div>
                                <h3 className="fw-bold text-white">Évaluer {selectedFournisseur?.raison_sociale}</h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-2">
                        <Form>
                            <Form.Group className="mb-4">
                                <Form.Label className="fw-semibold">Note globale</Form.Label>
                                <div className="d-flex justify-content-center gap-3">
                                    {[1,2,3,4,5].map(n => (
                                        <Button
                                            key={n}
                                            variant={evaluation.note >= n ? "warning" : "outline-warning"}
                                            onClick={() => setEvaluation({...evaluation, note: n})}
                                            className="rounded-circle p-2"
                                            style={{ width: '50px', height: '50px', fontSize: '1.2rem' }}
                                        >
                                            {n}
                                        </Button>
                                    ))}
                                </div>
                            </Form.Group>

                            <Row className="g-3 mb-3">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="small">Délais</Form.Label>
                                        <Form.Select 
                                            value={evaluation.critere_delai}
                                            onChange={(e) => setEvaluation({...evaluation, critere_delai: parseInt(e.target.value)})}
                                            className="rounded-pill"
                                        >
                                            <option value="5">⭐ Excellent</option>
                                            <option value="4">⭐ Bon</option>
                                            <option value="3">⭐ Moyen</option>
                                            <option value="2">⭐ Médiocre</option>
                                            <option value="1">⭐ Mauvais</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="small">Qualité</Form.Label>
                                        <Form.Select 
                                            value={evaluation.critere_qualite}
                                            onChange={(e) => setEvaluation({...evaluation, critere_qualite: parseInt(e.target.value)})}
                                            className="rounded-pill"
                                        >
                                            <option value="5">⭐ Excellent</option>
                                            <option value="4">⭐ Bon</option>
                                            <option value="3">⭐ Moyen</option>
                                            <option value="2">⭐ Médiocre</option>
                                            <option value="1">⭐ Mauvais</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="small">Prix</Form.Label>
                                        <Form.Select 
                                            value={evaluation.critere_prix}
                                            onChange={(e) => setEvaluation({...evaluation, critere_prix: parseInt(e.target.value)})}
                                            className="rounded-pill"
                                        >
                                            <option value="5">💰 Très compétitif</option>
                                            <option value="4">💰 Compétitif</option>
                                            <option value="3">💰 Dans la moyenne</option>
                                            <option value="2">💰 Élevé</option>
                                            <option value="1">💰 Très élevé</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-3">
                                <Form.Label className="small">Commentaire</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={evaluation.commentaire}
                                    onChange={(e) => setEvaluation({...evaluation, commentaire: e.target.value})}
                                    placeholder="Votre avis sur ce fournisseur..."
                                    className="rounded-3"
                                />
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 justify-content-center">
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => setShowEvalModal(false)}
                            className="px-4 rounded-pill"
                            disabled={evalLoading}
                        >
                            <i className="ri-close-line me-2"></i>Annuler
                        </Button>
                        <Button 
                            variant="warning" 
                            onClick={submitEvaluation} 
                            className="px-5 rounded-pill"
                            disabled={evalLoading}
                        >
                            {evalLoading ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Enregistrement...
                                </>
                            ) : (
                                <>
                                    <i className="ri-star-line me-2"></i>Évaluer
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }

                    .fade-in {
                        animation: fadeIn 0.5s ease;
                    }

                    .stats-row .stat-card {
                        transition: all 0.3s ease;
                        border-radius: 16px;
                    }

                    .stats-row .stat-card:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 15px 30px rgba(0,0,0,0.1) !important;
                    }

                    .filter-card {
                        border-radius: 16px;
                        background: #f8fafc;
                    }

                    .table-card {
                        border-radius: 16px;
                        overflow: hidden;
                    }

                    .custom-table thead th {
                        background: #f8fafc;
                        color: #1e293b;
                        font-weight: 600;
                        font-size: 0.85rem;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        border-bottom: 2px solid #e2e8f0;
                    }

                    .custom-table tbody tr {
                        transition: all 0.2s ease;
                    }

                    .custom-table tbody tr:hover {
                        background: #f1f5f9 !important;
                    }

                    .action-btn {
                        opacity: 0.8;
                        transition: all 0.2s ease;
                    }

                    .action-btn:hover {
                        opacity: 1;
                        transform: translateY(-2px);
                    }

                    .sortable-header:hover {
                        color: #4361ee;
                    }

                    .modern-modal .modal-content {
                        border: none;
                        border-radius: 24px;
                        box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                    }

                    .confirm-icon {
                        animation: bounceIn 0.5s ease-out;
                    }

                    @keyframes bounceIn {
                        0% { transform: scale(0); opacity: 0; }
                        50% { transform: scale(1.1); }
                        100% { transform: scale(1); opacity: 1; }
                    }

                    .font-mono {
                        font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
                    }
                `}</style>

                <Footer />
            </div>
        </>
    );
}